import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
} from 'lucide-react';
import { API } from '@/lib/apiBase';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { TutorialStatusBadge } from './TutorialStatusBadge';
import { effectiveHelpStatusFromTutorial } from './helpStatus';

const CENTRALE_BASE =
  (process.env.REACT_APP_CENTRALE_URL || 'https://app.prysme.eu').replace(
    /\/$/,
    '',
  );

/** Base URL plein écran Prysma (Mongo) ou secours Centrale + /aide/tutoriel/{id}. */
function resolveTutorialFullViewBaseUrl(detail, tutorialId) {
  const raw = String(detail?.full_view_url ?? detail?.fullViewUrl ?? '').trim();
  if (raw) return raw;
  if (tutorialId) {
    return `${CENTRALE_BASE}/aide/tutoriel/${encodeURIComponent(String(tutorialId))}`;
  }
  return '';
}

/** Ajoute ?step=N ou &step=N (N ≥ 1). */
function tutorialFullViewUrlWithStep(baseUrl, stepOneBased) {
  const n = Math.max(1, Math.floor(Number(stepOneBased)) || 1);
  let abs = baseUrl;
  try {
    abs = new URL(baseUrl).href;
  } catch {
    try {
      const path = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`;
      abs = new URL(path, `${CENTRALE_BASE}/`).href;
    } catch {
      abs = baseUrl;
    }
  }
  try {
    const u = new URL(abs);
    u.searchParams.set('step', String(n));
    return u.toString();
  } catch {
    const sep = abs.includes('?') ? '&' : '?';
    return `${abs}${sep}step=${encodeURIComponent(String(n))}`;
  }
}

function effectiveHelpStatus(t) {
  if (!t) return null;
  return effectiveHelpStatusFromTutorial(t);
}

function revokeScreenshotCache(cache) {
  if (!cache) return;
  cache.forEach((entry) => {
    if (entry?.kind === 'blob' && entry.url) {
      URL.revokeObjectURL(entry.url);
    }
  });
  cache.clear();
}

const COMMENT_BUBBLE_STYLES = {
  attention: 'border-2 border-amber-500 bg-slate-800',
  important: 'border-2 border-red-500 bg-slate-800',
  info: 'border-2 border-blue-500 bg-slate-800',
  default: 'border-2 border-blue-500 bg-slate-800',
};

function bubbleBorderClassForStepType(typeRaw) {
  const t = String(typeRaw || 'info').toLowerCase();
  if (t === 'attention') return COMMENT_BUBBLE_STYLES.attention;
  if (t === 'important') return COMMENT_BUBBLE_STYLES.important;
  if (t === 'info') return COMMENT_BUBBLE_STYLES.info;
  return COMMENT_BUBBLE_STYLES.default;
}

function TutorialHeaderMeta({
  titleId,
  titre,
  loading,
  helpStatus,
  displayTheme,
  description,
}) {
  return (
    <div className="mt-4 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <h2
            id={titleId}
            className="min-w-0 truncate text-lg font-semibold leading-snug text-foreground"
          >
            {loading ? '…' : titre}
          </h2>
          <Eye
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            strokeWidth={2}
            aria-hidden
          />
        </div>
        <div className="shrink-0">
          {!loading && helpStatus === 'a_jour' ? (
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              Parcours terminé
            </span>
          ) : !loading && helpStatus ? (
            <TutorialStatusBadge status={helpStatus} />
          ) : null}
        </div>
      </div>
      <p className="text-sm font-normal text-muted-foreground">
        Thème : {displayTheme}
      </p>
      {!loading && description ? (
        <p className="text-sm font-normal leading-snug text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function bubblePositionStyle(step) {
  if (!step || typeof step !== 'object') return null;
  const left =
    step.bubble_left_pct ??
    step.left_pct ??
    step.anchor_left_pct ??
    step.position_left;
  const top =
    step.bubble_top_pct ??
    step.top_pct ??
    step.anchor_top_pct ??
    step.position_top;
  if (left == null || top == null) return null;
  const l = Number(left);
  const t = Number(top);
  if (!Number.isFinite(l) || !Number.isFinite(t)) return null;
  return {
    left: `${l}%`,
    top: `${t}%`,
    transform: 'translate(-50%, -50%)',
  };
}

function TutorialDetailFooter({
  safeIdx,
  lastStep,
  onPrev,
  onNext,
  onComplete,
  onEnlarge,
  enlargeDisabled,
}) {
  return (
    <div className="mt-3 grid w-full grid-cols-3 items-center gap-2 border-t border-border bg-background py-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={safeIdx <= 0}
        className="justify-self-start border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-40 [&_svg]:h-3.5 [&_svg]:w-3.5"
        onClick={onPrev}
      >
        <ChevronLeft className="shrink-0" strokeWidth={2} />
        Précédent
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={enlargeDisabled}
        className="justify-self-center border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground"
        onClick={onEnlarge}
      >
        Agrandir
      </Button>
      {lastStep ? (
        <Button
          type="button"
          size="sm"
          variant="default"
          className="justify-self-end font-semibold"
          onClick={onComplete}
        >
          Parcours terminé
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="default"
          className="justify-self-end gap-1"
          onClick={onNext}
        >
          Suivant
          <ChevronRight className="h-4 w-4 shrink-0" strokeWidth={2} />
        </Button>
      )}
    </div>
  );
}

export function TutorialDetailView({
  tutorialId,
  themeNom,
  token,
  onBack,
  onCloseHelp,
  onTutorialProgressChanged,
  asideClassName,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [imageSrc, setImageSrc] = useState(null);
  const [screenshotEpoch, setScreenshotEpoch] = useState(0);
  const screenshotCacheRef = useRef(new Map());
  const prefetchGenRef = useRef(0);
  const autoCompleteSentRef = useRef(false);

  const markParcoursComplete = useCallback(async () => {
    if (!token || !tutorialId) return;
    try {
      await axios.post(
        `${API}/help/video-tutorials/mark-viewed`,
        { tutorial_id: tutorialId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const nowIso = new Date().toISOString();
      setDetail((d) =>
        d
          ? {
              ...d,
              help_status: 'a_jour',
              fully_viewed_at: nowIso,
            }
          : d
      );
      onTutorialProgressChanged?.();
    } catch {
      /* fin de parcours locale même si persistance indisponible */
    }
  }, [token, tutorialId, onTutorialProgressChanged]);

  useEffect(
    () => () => {
      revokeScreenshotCache(screenshotCacheRef.current);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token || !tutorialId) return;
      setLoading(true);
      setError(null);
      setStepIndex(0);
      try {
        const res = await axios.get(
          `${API}/help/video-tutorials/${encodeURIComponent(tutorialId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setDetail(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e.response?.status === 403
              ? 'Vous n’avez pas accès à ce tutoriel.'
              : 'Impossible de charger le tutoriel.'
          );
          setDetail(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tutorialId, token]);

  useEffect(() => {
    autoCompleteSentRef.current = false;
  }, [tutorialId]);

  const steps = Array.isArray(detail?.commentaires) ? detail.commentaires : [];
  const safeIdx = Math.min(
    stepIndex,
    Math.max(steps.length - 1, 0)
  );
  const current = steps.length > 0 ? steps[safeIdx] : null;

  useEffect(() => {
    if (!detail || !token || !tutorialId) {
      revokeScreenshotCache(screenshotCacheRef.current);
      screenshotCacheRef.current = new Map();
      setImageSrc(null);
      return undefined;
    }

    const myGen = ++prefetchGenRef.current;
    const list = Array.isArray(detail.commentaires) ? detail.commentaires : [];
    const next = new Map();

    const commitCache = (map) => {
      const prev = screenshotCacheRef.current;
      screenshotCacheRef.current = map;
      setScreenshotEpoch((e) => e + 1);
      setTimeout(() => {
        if (myGen === prefetchGenRef.current) {
          revokeScreenshotCache(prev);
        }
      }, 0);
    };

    const pending = [];
    for (const s of list) {
      if (!s?.id) continue;
      const id = String(s.id);
      if (s.screenshot_is_remote && s.screenshot_url) {
        next.set(id, { kind: 'remote', url: s.screenshot_url });
      } else if (s.has_screenshot) {
        pending.push(id);
      }
    }

    if (pending.length === 0) {
      commitCache(next);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        pending.map((id) =>
          axios
            .get(
              `${API}/help/video-tutorials/${encodeURIComponent(tutorialId)}/screenshots/${encodeURIComponent(id)}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
              }
            )
            .then((res) => ({ id, blob: res.data }))
            .catch(() => ({ id, blob: null }))
        )
      );

      if (cancelled || myGen !== prefetchGenRef.current) return;

      const blobEntries = [];
      for (const { id, blob } of results) {
        if (blob instanceof Blob && blob.size > 0) {
          blobEntries.push({ id, blob });
        }
      }
      if (myGen !== prefetchGenRef.current) return;

      for (const { id, blob } of blobEntries) {
        next.set(id, { kind: 'blob', url: URL.createObjectURL(blob) });
      }
      commitCache(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [detail, token, tutorialId]);

  useEffect(() => {
    if (!current?.id) {
      setImageSrc(null);
      return;
    }
    const id = String(current.id);
    const entry = screenshotCacheRef.current.get(id);
    if (entry?.url) {
      setImageSrc(entry.url);
    }
  }, [current?.id, screenshotEpoch]);

  /* Dès la dernière étape : enregistre la fin de parcours (sans exiger uniquement le clic « Parcours terminé »). */
  useEffect(() => {
    if (!detail || !token || !tutorialId || loading) return;
    if (steps.length === 0) return;
    if (safeIdx !== steps.length - 1) return;
    if (effectiveHelpStatusFromTutorial(detail) === 'a_jour') return;
    if (autoCompleteSentRef.current) return;
    autoCompleteSentRef.current = true;
    void markParcoursComplete();
  }, [
    detail,
    token,
    tutorialId,
    loading,
    steps.length,
    safeIdx,
    markParcoursComplete,
  ]);

  const helpStatus = effectiveHelpStatus(detail);
  const titre = detail?.titre || 'Tutoriel';
  const description = detail?.description || '';
  const displayTheme = detail?.theme_nom || themeNom || '—';
  const lastStep = steps.length === 0 || safeIdx >= steps.length - 1;
  const bubbleShellClass = bubbleBorderClassForStepType(current?.type);
  const bubbleCoords = bubblePositionStyle(current);

  const progressRatio =
    steps.length > 0 ? (safeIdx + 1) / steps.length : 0;

  const openTutorialFullViewInNewTab = useCallback(() => {
    if (!tutorialId || steps.length === 0) return;
    const base = resolveTutorialFullViewBaseUrl(detail, tutorialId);
    if (!base) return;
    const url = tutorialFullViewUrlWithStep(base, safeIdx + 1);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [detail, tutorialId, steps.length, safeIdx]);

  const stepCardBlock = imageSrc || steps.length > 0 ? (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="relative w-full overflow-hidden bg-muted/50">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            className="block w-full object-contain object-top"
          />
        ) : (
          <div className="min-h-[200px] w-full bg-muted/40" aria-hidden />
        )}
        {bubbleCoords ? (
          <div
            className={cn(
              'pointer-events-none absolute z-10 max-w-[min(92%,280px)] rounded-lg p-3 text-sm font-medium leading-snug text-slate-100 shadow-xl',
              bubbleShellClass,
            )}
            style={bubbleCoords}
          >
            {current?.bulle_texte ||
              current?.overlay_text ||
              current?.texte ||
              '—'}
          </div>
        ) : null}
        <div
          className="pointer-events-none absolute bottom-4 right-3 top-4 w-1.5 overflow-hidden rounded-full bg-white/90 dark:bg-white/20"
          aria-hidden
        >
          <div
            className="absolute left-0 right-0 top-0 rounded-full bg-primary transition-[height] duration-300 ease-out"
            style={{ height: `${progressRatio * 100}%` }}
          />
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs font-normal text-muted-foreground">
          Étape {safeIdx + 1} / {steps.length}
        </p>
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground">
          {current?.texte || '—'}
        </p>
      </div>
    </div>
  ) : null;

  return (
    <>
      <aside
        data-testid="help-tutorial-detail"
        className={cn(
          'sticky top-0 flex h-screen max-h-screen min-h-0 min-w-[300px] w-[min(25vw,540px)] max-w-full shrink-0 flex-col overflow-hidden rounded-t-2xl border-l border-border bg-background shadow-lg',
          asideClassName
        )}
        aria-labelledby="tutorial-detail-title"
      >
        <header className="shrink-0 border-b border-border bg-background px-6 pb-4 pt-4">
          <div className="flex items-start justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              Retour
            </Button>
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground dark:hover:bg-[hsl(217,33%,17%)]"
              onClick={onCloseHelp}
              aria-label="Fermer le centre d'aide"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2} />
            </button>
          </div>
          <TutorialHeaderMeta
            titleId="tutorial-detail-title"
            titre={titre}
            loading={loading}
            helpStatus={helpStatus}
            displayTheme={displayTheme}
            description={description}
          />
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain px-6 pb-4 pt-2">
            {loading && (
              <div
                className="mb-4 h-3 w-2/3 max-w-[220px] animate-pulse rounded-md bg-muted"
                aria-hidden
              />
            )}
            {!loading && error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            {!loading && !error && detail && (
              <>
                {steps.length === 0 ? (
                  <p className="text-xs font-normal text-muted-foreground">
                    Aucune étape avec capture aplatie (
                    <code className="text-xs">screenshot_flattened</code>) pour
                    ce tutoriel.
                  </p>
                ) : (
                  <>
                    {stepCardBlock}
                    <TutorialDetailFooter
                      safeIdx={safeIdx}
                      lastStep={lastStep}
                      onPrev={() => setStepIndex((i) => Math.max(0, i - 1))}
                      onNext={() =>
                        setStepIndex((i) =>
                          Math.min(steps.length - 1, i + 1),
                        )
                      }
                      onComplete={async () => {
                        await markParcoursComplete();
                        onBack();
                      }}
                      onEnlarge={openTutorialFullViewInNewTab}
                      enlargeDisabled={steps.length === 0}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
